import { Editor, Extension } from '@tiptap/core'
import { ReactRenderer } from '@tiptap/react'
import Suggestion, { SuggestionProps, SuggestionKeyDownProps } from '@tiptap/suggestion'
import { PluginKey } from '@tiptap/pm/state'

import GROUPS from './groups'
import { MenuDisplay } from './MenuDisplay'
import {
  createGetReferenceClientRect,
  updatePopupPosition,
  createPopupElement,
  DEFAULT_RECT_STORAGE,
  type RectStorage,
} from '../suggestionPositioning'

const extensionName = 'colonCommand'

let popupElement: HTMLDivElement | null = null

declare module '@tiptap/core' {
  interface Storage {
    colonCommand: {
      rect: RectStorage
    }
  }
}

export const ColonCommand = Extension.create({
  name: extensionName,

  priority: 200,

  onCreate() {
    // Create popup element for Floating UI
    if (!popupElement) {
      popupElement = createPopupElement('colon-command-popup')
    }
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        char: ':',
        allowSpaces: true,
        startOfLine: true,
        pluginKey: new PluginKey(extensionName),
        allow: ({ state, range }) => {
          const $from = state.doc.resolve(range.from)
          const isRootDepth = $from.depth === 1
          const isParagraph = $from.parent.type.name === 'paragraph'
          const isStartOfNode = $from.parent.textContent?.charAt(0) === ':'
          // TODO
          const isInColumn = this.editor.isActive('column')

          const afterContent = $from.parent.textContent?.substring($from.parent.textContent?.indexOf(':'))
          const isValidAfterContent = !afterContent?.endsWith('  ')

          return (
            ((isRootDepth && isParagraph && isStartOfNode) || (isInColumn && isParagraph && isStartOfNode)) &&
            isValidAfterContent
          )
        },
        command: ({ editor, props }: { editor: Editor; props: any }) => {
          const { view, state } = editor
          const { $head, $from } = view.state.selection

          const end = $from.pos
          const from = $head?.nodeBefore
            ? end - ($head.nodeBefore.text?.substring($head.nodeBefore.text?.indexOf(':')).length ?? 0)
            : $from.start()

          const tr = state.tr.deleteRange(from, end)
          view.dispatch(tr)

          props.action(editor)
          view.focus()
        },
        items: ({ query }: { query: string }) => {
          const withFilteredCommands = GROUPS.map(group => ({
            ...group,
            symbols: group.symbols
              .filter(symbol => {
                const labelNormalized = symbol.label.toLowerCase().trim()
                const queryNormalized = query.toLowerCase().trim()

                return labelNormalized.includes(queryNormalized)
              })
          }))        

          const withoutEmptyGroups = withFilteredCommands.filter(group => {
            if (group.symbols.length > 0) {
              return true
            }

            return false
          })

          const withEnabledSettings = withoutEmptyGroups.map(group => ({
            ...group,
            symbols: group.symbols.map(symbol => ({
              ...symbol,
              isEnabled: true,
            })),
          }))

          return withEnabledSettings
        },
        render: () => {
          let component: any
          let scrollHandler: (() => void) | null = null

          return {
            onStart: (props: SuggestionProps) => {
              component = new ReactRenderer(MenuDisplay, {
                props,
                editor: props.editor,
              })

              const { view } = props.editor
              const getReferenceClientRect = createGetReferenceClientRect(props, extensionName)
              const updatePosition = () => updatePopupPosition(popupElement, getReferenceClientRect)

              scrollHandler = updatePosition

              if (popupElement) {
                // Determine if we need to move the popup to the editor's parent container for proper positioning and scroll behavior
                const editorParent = view.dom.parentElement;
                if (editorParent && popupElement.parentElement !== editorParent) {
                   editorParent.appendChild(popupElement);
                }

                popupElement.innerHTML = ''
                popupElement.appendChild(component.element)
                popupElement.style.display = 'block'
                updatePosition()
              }

              view.dom.parentElement?.addEventListener('scroll', scrollHandler)
            },

            onUpdate(props: SuggestionProps) {
              component.updateProps(props)

              const getReferenceClientRect = createGetReferenceClientRect(props, extensionName)
              updatePopupPosition(popupElement, getReferenceClientRect)

              // eslint-disable-next-line no-param-reassign
              props.editor.storage[extensionName].rect = props.clientRect
                ? getReferenceClientRect()
                : DEFAULT_RECT_STORAGE
            },

            onKeyDown(props: SuggestionKeyDownProps) {
              if (props.event.key === 'Escape') {
                if (popupElement) {
                  popupElement.style.display = 'none'
                }
                return true
              }

              if (popupElement && popupElement.style.display === 'none') {
                popupElement.style.display = 'block'
              }

              return component.ref?.onKeyDown(props)
            },

            onExit(props) {
              if (popupElement) {
                popupElement.style.display = 'none'
                popupElement.innerHTML = ''
              }
              if (scrollHandler) {
                const { view } = props.editor
                view.dom.parentElement?.removeEventListener('scroll', scrollHandler)
              }
              component.destroy()
            },
          }
        },
      }),
    ]
  },

  addStorage() {
    return {
      rect: DEFAULT_RECT_STORAGE,
    }
  },
})

export default ColonCommand